import React from "react";
import { FileText, ExternalLink } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { founders, board, collaborations } from "../mock/mock";

const STATUTE_URL =
  "https://www.arts-4all.com/_files/ugd/0de1ea_144a3f01344849208fecb38f0ba8385d.pdf";

const ChiSiamo = () => {
  const { t, pick } = useLang();

  return (
    <div>
      <PageHero
        image="https://customer-assets.emergentagent.com/job_arte-natura-hub/artifacts/sdevkhkj_52597996_2487457811491340_4595331182396178432_n.jpg"
        overlay="from-black/40 via-black/20 to-black/65"
        kicker={t("common.since")}
        title={t("about.title")}
        lead={t("about.lead")}
      />

      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-8 space-y-6">
          <Reveal>
            <p className="text-[#3d3a35] text-lg leading-relaxed">{t("about.p1")}</p>
          </Reveal>
          <Reveal delay={100}>
            <p className="text-[#3d3a35] text-lg leading-relaxed">{t("about.p2")}</p>
          </Reveal>
        </div>
      </section>

      {/* BOARD OF DIRECTORS (current) */}
      <section className="bg-white py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("about.boardTitle")}
            </h2>
            <p className="mt-4 text-[#6a655c]">{t("about.boardLead")}</p>
          </Reveal>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
            {board.map((f, i) => (
              <Reveal key={f.name} delay={(i % 3) * 100} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-[#eae4d9]">
                  <img
                    src={f.image}
                    alt={f.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top grayscale-[15%] transition-all duration-[900ms] group-hover:grayscale-0 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-[#1c1b1a]">{f.name}</h3>
                <span className="text-[#b5533a] text-xs uppercase tracking-[0.18em]">
                  {pick(f.role)}
                </span>
                {pick(f.bio) && (
                  <p className="mt-3 text-sm leading-relaxed text-[#6a655c]">{pick(f.bio)}</p>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDERS */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("about.foundersTitle")}
            </h2>
            <p className="mt-4 text-[#6a655c]">{t("about.foundersLead")}</p>
          </Reveal>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {founders.map((f, i) => (
              <Reveal key={f.name} delay={(i % 3) * 100} className="group">
                <div className="aspect-[4/5] overflow-hidden bg-[#eae4d9]">
                  <img
                    src={f.image}
                    alt={f.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-top grayscale-[15%] transition-all duration-[900ms] group-hover:grayscale-0 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-5 font-serif text-2xl text-[#1c1b1a]">{f.name}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COLLABORATIONS */}
      <section className="bg-[#faf7f2] py-20 lg:py-28">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="font-serif text-4xl md:text-5xl text-[#1c1b1a]">
              {t("about.collabsTitle")}
            </h2>
            <p className="mt-4 text-[#6a655c]">{t("about.collabsLead")}</p>
          </Reveal>

          <div className="grid gap-8 md:grid-cols-2">
            {collaborations.map((c, i) => (
              <Reveal
                key={i}
                delay={i * 120}
                className="bg-white border border-[#eae4d9] p-8 flex flex-col"
              >
                <h3 className="font-serif text-2xl text-[#1c1b1a]">{c.name}</h3>
                <span className="text-[#b5533a] text-xs uppercase tracking-[0.18em] mt-1">
                  {c.subtitle}
                </span>
                <p className="mt-4 text-sm leading-relaxed text-[#6a655c] flex-1">
                  {pick(c.desc)}
                </p>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-6 inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-[#b5533a] hover:gap-3 transition-all"
                >
                  {t("about.collabsVisit")}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATUTE */}
      <section className="bg-[#1c1b1a] py-20">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="max-w-lg text-center md:text-left">
            <h2 className="font-serif text-3xl md:text-4xl text-white">
              {t("about.statuteTitle")}
            </h2>
            <p className="mt-3 text-[#c7c0b4]">{t("about.statuteText")}</p>
          </div>
          <a
            href={STATUTE_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center gap-3 bg-[#b5533a] hover:bg-[#a0472f] text-white px-7 py-4 text-sm uppercase tracking-[0.14em] transition-colors"
          >
            <FileText className="w-5 h-5" />
            {t("about.statuteBtn")}
          </a>
        </div>
      </section>
    </div>
  );
};

export default ChiSiamo;
